import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SpaLink from './SpaLink';
import ProductsMenu from './ProductsMenu';
import { HOME_META } from './products';   // Nov 1, 2026 — one home meta for every page
import { useAuth } from '../context/AuthContext';
import { SAMPLE_ROWS, SAMPLE_BOARD, SAMPLE_TEAM_NUMBER, SAMPLE_SCORING_PATH, SAMPLE_SCORING_SHORT, SAMPLE_BOARD_PATH } from '../sampleEvent';

/**
 * EventsPage — the public /events landing page.
 *
 * Built to the approved v3 mockup (claude/Events_Landing_Page_Mockup_Sep16.md)
 * with the Sept 17–18 decisions applied:
 *   - Both "Coming" blocks are gone. Card message + sponsor logos are shelved
 *     (GTM plan §0); "Results in minutes" (CTP + results email) is unbuilt.
 *     Do not re-add either as a promise — add them the week they ship.
 *   - The hero, the TV panel, the printed card and the cart sign all show ONE
 *     invented event (src/sampleEvent.js), labelled as an example. Nothing is
 *     fetched, so nothing expires. The QR on the card and the sign is real and
 *     lands on /try/scoring; the board links open /try/leaderboard, which is the
 *     production TV renderer fed the same fixture. (Sept 20, 2026 — replaced the
 *     live-fetch-with-fallback that had been showing the fallback since Sept 18.)
 *   - Games copy lists only what exists. Oct 5, 2026: captain's choice
 *     (scramble), stroke play (gross / net / both), best ball (2-man, best
 *     1/2/3 of 4, blind draw for a short team) and shamble, with skins, closest
 *     to the pin and Stableford alongside. Stableford is a side game here: the
 *     event leaderboard and TV view rank by strokes, not points — don't offer it
 *     as the event's format until they do. Flights are NOT built.
 *   - The QR / six-digit-code door works on TEAM formats only (teamAccessService
 *     refuses NOT_TEAM_SCORED; printService draws the QR only when isTeam). Until
 *     individual rounds get a QR, the doors copy says so — do not widen it.
 *   - Pricing: $135 (<=72) / $185 (<=250) standalone, $85 for a subscriber on
 *     Professional or above, League annual includes one event a year, and
 *     Club & Association launches with no price.
 *
 *   - Sept 30, 2026 (Event Pass slice 4): the pricing cards BUY instead of
 *     "Talk to us". Event Pass → /events/start (EventPassStart.js: pick the
 *     pass, name the event, pay — no plan picker) or, when signed in, straight
 *     to the window on /dashboard. "Add an event to your group" → /dashboard (login first if
 *     needed) with the window opening. Both use the tto_open_event_pass flag
 *     that App.js EventPassModalHost reads once. Club & Association stays
 *     "Talk to us". Promise copy matches Event_Pass_Decisions_Sep28.md: one
 *     event within 6 months, used up when finalized, results viewable 30 days.
 *
 *   - Sept 25, 2026: "See it in action" section — the Event Setup and Event
 *     Desk videos (YouTube). Click-to-play: only the thumbnail loads until the
 *     visitor presses play, then a youtube-nocookie iframe replaces it. A video
 *     with no youtubeId is simply not shown, and VideoObject JSON-LD is emitted
 *     only for videos that have one.
 *
 * Conventions follow the other marketing pages (LandingPage.js, FoundersPage.js):
 * inline style objects, an isMobile flag rather than CSS media queries, SpaLink
 * for internal links, and window.gtag for GA4. The page is self-contained — no
 * other component imports from it.
 */

// ---------------------------------------------------------------------------
// The sample event. One fixture (src/sampleEvent.js) feeds the hero phone, the
// TV panel, the printed card, the cart sign, and the two /try screens, so the
// same teams appear everywhere a prospect looks. A prospect does not care
// whether it is live; they care that it looks like the product, never rots, and
// that the QR they lift their phone to actually goes somewhere (Dennis, Sept 20).
// ---------------------------------------------------------------------------
const PUBLIC_BOARD_HOST = 'TTOleaderboard.com';
const TEAM_CODE_HOST = 'ttogolf.com';
const SAMPLE_BOARD_URL = SAMPLE_BOARD_PATH;      // same app — the TV renderer against the fixture
const SAMPLE_SCORING_LINK = SAMPLE_SCORING_PATH;  // what the buttons open; the QR encodes the absolute form

// A date for the printed pieces that is never in the past: the next Saturday.
const SAMPLE_DATE_TEXT = (() => {
  const d = new Date(); d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
})();

// GA4 helper — safe when gtag isn't loaded.
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

const colors = {
  green900: '#14532d', green800: '#166534', green700: '#15803d', green600: '#16a34a',
  green100: '#dcfce7', green50: '#f0fdf4',
  gray900: '#111827', gray800: '#1f2937', gray700: '#374151', gray600: '#4b5563',
  gray500: '#6b7280', gray400: '#9ca3af', gray300: '#d1d5db', gray200: '#e5e7eb',
  gray100: '#f3f4f6', gray50: '#f9fafb',
  amber500: '#f59e0b', amber400: '#fbbf24', amber300: '#fcd34d',
  paper: '#fbfaf4', paperInk: '#1f2937', pencil: '#334155', startHole: '#fde68a',
};

const SERIF = "'DM Serif Display', Georgia, 'Times New Roman', serif";
const SANS = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const HAND = "'Bradley Hand', 'Segoe Print', 'Comic Sans MS', cursive";

// ---------------------------------------------------------------------------
// Per-route meta. index.html holds the site defaults (single source of truth);
// this page overrides them on mount and puts them back on unmount so a
// client-side navigation home doesn't leave the /events canonical behind.
// The prerenderer captures the DOM after render, so the prerendered
// /events/index.html ships with these tags baked in.
// ---------------------------------------------------------------------------
const EVENTS_META = {
  title: 'Golf Tournament Software for Event Day | Tee Time Organizer',
  description:
    'Run your golf tournament’s day — scramble, stroke play or best ball: shotgun pairings in minutes, printed scorecards and cart signs, scoring on players’ phones with no app, and a live leaderboard on every phone and the clubhouse TV. One flat price per event — we never touch your entry fees.',
  url: 'https://teetimeorganizer.net/events',
};

const EVENTS_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Tee Time Organizer Event Pass',
  description:
    'Everything a golf tournament needs on the day: field upload, shotgun pairings, printed scorecards and cart signs, team scan-to-score codes, live leaderboard and clubhouse TV view, and results — captain’s choice, stroke play or best ball, with USGA handicap allowances. Registration and entry fees stay with the organizer.',
  brand: { '@type': 'Brand', name: 'Tee Time Organizer' },
  url: 'https://teetimeorganizer.net/events',
  category: 'Golf tournament management software',
  offers: [
    {
      '@type': 'Offer',
      name: 'Event Pass — up to 72 players',
      price: '135',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: 'https://teetimeorganizer.net/events#pricing',
    },
    {
      '@type': 'Offer',
      name: 'Event Pass — up to 250 players',
      price: '185',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: 'https://teetimeorganizer.net/events#pricing',
    },
    {
      '@type': 'Offer',
      name: 'Event Pass — Tee Time Organizer subscriber',
      price: '85',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: 'https://teetimeorganizer.net/events#pricing',
    },
  ],
};

// ---------------------------------------------------------------------------
// "See it in action" videos (Sept 25, 2026). Thumbnails live in
// frontend/public/video-thumbs/ (the same art as the YouTube thumbnails).
// A video whose youtubeId is empty is not rendered at all.
// ---------------------------------------------------------------------------
const EVENT_VIDEOS = [
  {
    key: 'setup',
    // Oct 12, 2026: re-made (2:45) to include the event page and the Event setup link
    // on the card. The old video (Oi4HKgeSvts, 4:18) is unlisted. ?v=2 busts the old thumbnail.
    youtubeId: '7bkrginDJ6M',
    kicker: 'Before the day',
    title: 'Setting up an event',
    description:
      'Create the tee time and set up the event, build its own event page where golfers sign up, then pick the format, balance the pairings, run the Ready check and print.',
    duration: '2:45',
    isoDuration: 'PT2M45S',
    thumb: '/video-thumbs/event-setup.jpg?v=2',
    schemaName: 'Tee Time Organizer: Setting Up an Event',
    uploadDate: '2026-10-12',
  },
  {
    key: 'desk',
    // Oct 12, 2026: re-voiced (Adam) with the current Event Home page (3:55).
    // The earlier versions (Jok8xsrvuxE, GIuxlPJUI-U) are unlisted.
    youtubeId: '1rklAGucEcY',
    kicker: 'On the day',
    title: 'Event day at the desk',
    description:
      'Open the desk with a link or code, print cards and cart signs, let players score by QR code with no app, fix a score, and put the live board on the clubhouse TV.',
    duration: '3:55',
    isoDuration: 'PT3M55S',
    thumb: '/video-thumbs/event-desk.jpg',
    schemaName: 'Tee Time Organizer: Event Day at the Desk',
    uploadDate: '2026-10-12',
  },
];
// The promo (Oct 1, 2026; re-cut Oct 12 as the Last Chance version with the event page,
// 1:15): featured on its own just under the hero, ahead of the two how-to videos.
// Thumbnail is the clubhouse-TV still (?v=2 busts the cached Lowcountry version).
// Oct 12, 2026 (oct12i): re-uploaded with the new winners photo; 0bzpJm5sGjA is unlisted.
const PROMO_VIDEO = {
  key: 'promo',
  youtubeId: 'LIJn-_Rdp3A',
  kicker: 'Watch · 1:15',
  title: 'Tournament day, start to finish',
  description:
    'Your own event page and sign-up, a balanced shotgun, printed cards with a QR code, scores entered on phones, a live leaderboard on the clubhouse TV, and results the moment the last group is in.',
  duration: '1:15',
  isoDuration: 'PT1M15S',
  thumb: '/video-thumbs/events-promo.jpg?v=2',
  schemaName: 'Tee Time Organizer: Run Your Golf Tournament From Registration to Results',
  uploadDate: '2026-10-12',
};
const PLAYER_TOUR_URL = 'https://youtu.be/JnT08sUhqKI';
const SITE_ORIGIN = 'https://teetimeorganizer.net';

const buildVideoJsonLd = (videos) => videos.map((v) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: v.schemaName,
  description: v.description,
  thumbnailUrl: [`${SITE_ORIGIN}${v.thumb}`],
  uploadDate: v.uploadDate,
  duration: v.isoDuration,
  embedUrl: `https://www.youtube.com/embed/${v.youtubeId}`,
  url: `https://www.youtube.com/watch?v=${v.youtubeId}`,
  publisher: { '@type': 'Organization', name: 'Tee Time Organizer', url: SITE_ORIGIN },
}));

const setMetaByName = (name, content) => {
  const el = document.querySelector(`meta[name="${name}"]`);
  if (el) el.setAttribute('content', content);
};
const setMetaByProperty = (property, content) => {
  const el = document.querySelector(`meta[property="${property}"]`);
  if (el) el.setAttribute('content', content);
};

const applyMeta = (meta) => {
  document.title = meta.title;
  setMetaByName('title', meta.title);
  setMetaByName('description', meta.description);
  setMetaByName('twitter:title', meta.title);
  setMetaByName('twitter:description', meta.description);
  setMetaByName('twitter:url', meta.url);
  setMetaByProperty('og:title', meta.title);
  setMetaByProperty('og:description', meta.description);
  setMetaByProperty('og:url', meta.url);
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', meta.url);
};

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

/**
 * A REAL QR code, not a decorative one (Dennis, Sept 20, 2026): "any image where
 * a QR code exists should resolve to a hole-1 view on their phone if they try it
 * out." It encodes SAMPLE_SCORING_URL from sampleEvent.js — absolute, because a
 * phone camera has no idea what page the picture is on — and lands on
 * /try/scoring, the example scoring screen. Generated once with the `qrcode`
 * package (errorCorrectionLevel M, margin 1) and inlined as a path so the page
 * needs no runtime dependency and prerenders identically. Regenerate the path if
 * SAMPLE_SCORING_URL changes. On a desktop it is also a link, so nobody needs a
 * second device to try it.
 */
const SAMPLE_QR_VIEWBOX = '0 0 27 27';
const SAMPLE_QR_PATH = 'M1 1.5h7m2 0h1m2 0h2m2 0h1m1 0h7M1 2.5h1m5 0h1m4 0h6m1 0h1m5 0h1M1 3.5h1m1 0h3m1 0h1m1 0h1m1 0h2m2 0h1m3 0h1m1 0h3m1 0h1M1 4.5h1m1 0h3m1 0h1m1 0h2m1 0h5m2 0h1m1 0h3m1 0h1M1 5.5h1m1 0h3m1 0h1m1 0h3m2 0h1m2 0h1m1 0h1m1 0h3m1 0h1M1 6.5h1m5 0h1m1 0h3m3 0h2m2 0h1m5 0h1M1 7.5h7m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h7M9 8.5h1m2 0h2m1 0h1m1 0h1M1 9.5h1m1 0h5m2 0h2m1 0h2m4 0h5M1 10.5h1m2 0h3m2 0h1m1 0h2m1 0h1m3 0h1m1 0h1m3 0h1M1 11.5h1m1 0h2m2 0h1m1 0h1m3 0h4m1 0h2m2 0h1m1 0h2M1 12.5h4m1 0h1m1 0h2m2 0h2m1 0h1m1 0h1m1 0h2m4 0h1M1 13.5h1m3 0h1m1 0h1m5 0h4m1 0h4m1 0h3M1 14.5h1m1 0h1m1 0h1m9 0h1m1 0h1m2 0h1m1 0h1m1 0h1M1 15.5h1m1 0h1m1 0h4m1 0h4m1 0h2m1 0h1m1 0h3m1 0h2M1 16.5h1m3 0h2m2 0h4m3 0h3m1 0h2m3 0h1M1 17.5h1m4 0h3m1 0h1m1 0h1m1 0h2m1 0h5m1 0h1M9 18.5h1m1 0h1m1 0h1m1 0h3m3 0h2M1 19.5h7m6 0h1m2 0h1m1 0h1m1 0h1m1 0h3M1 20.5h1m5 0h1m1 0h2m2 0h1m3 0h1m3 0h2m1 0h1M1 21.5h1m1 0h3m1 0h1m1 0h3m1 0h9m1 0h3M1 22.5h1m1 0h3m1 0h1m1 0h1m4 0h2m1 0h3m1 0h5M1 23.5h1m1 0h3m1 0h1m1 0h1m1 0h3m3 0h1m4 0h2m1 0h1M1 24.5h1m5 0h1m2 0h3m2 0h8m2 0h1M1 25.5h7m1 0h1m1 0h3m4 0h1m1 0h6';

const SampleQr = ({ px }) => (
  <a
    href={SAMPLE_SCORING_PATH}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Try the example scoring screen (opens the screen a player sees after scanning their card)"
    title="Scan with your phone, or click, to try the scoring screen"
    onClick={() => trackEvent('events_try_scoring_click', { location: 'qr' })}
    style={{ display: 'block', width: px, height: px, lineHeight: 0 }}
  >
    <svg viewBox={SAMPLE_QR_VIEWBOX} shapeRendering="crispEdges" width="100%" height="100%" style={{ display: 'block', background: colors.paper }}>
      <path stroke={colors.gray900} d={SAMPLE_QR_PATH} />
    </svg>
  </a>
);

// The front nine of the sample course (src/sampleEvent.js) — Team 7 starts on 1A,
// so the pencilled holes are 1–5 and the scoring screen the QR opens is on Hole 1.
const HOLES = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const PARS = [4, 5, 4, 3, 4, 4, 3, 5, 4];
const HCPS = [9, 3, 13, 17, 1, 11, 15, 5, 7];
const PENCILLED = { 1: 3, 2: 4, 3: 4, 4: 3, 5: 4 };
const START_HOLE = 1;

/** The front-nine panel of a printed team scorecard. */
const PrintedScorecard = ({ isMobile }) => {
  const u = isMobile ? 9.5 : 12; // base type size for the card
  const cell = {
    borderRight: `0.8px solid ${colors.gray400}`,
    borderBottom: `0.8px solid ${colors.gray400}`,
    textAlign: 'center',
    padding: '0.3em 0',
    fontVariantNumeric: 'tabular-nums',
    minWidth: 0,
  };
  const labelCell = {
    ...cell,
    textAlign: 'left',
    paddingLeft: '0.45em',
    fontWeight: 700,
    fontSize: '0.86em',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };
  const headCell = { ...cell, fontWeight: 700, background: '#eef0ec' };

  const gridRow = (label, sub, values, opts = {}) => {
    const { total = '', isScore = false, isHead = false } = opts;
    const base = isHead ? headCell : cell;
    return (
      <>
        <div style={{ ...labelCell, ...(isScore ? { alignItems: 'flex-start', fontFamily: SANS } : null) }}>
          {label}
          {sub ? <small style={{ fontWeight: 400, color: colors.gray500, fontSize: '0.85em' }}>{sub}</small> : null}
        </div>
        {values.map((v, i) => (
          <div
            key={`${label}-${i}`}
            style={{
              ...base,
              ...(HOLES[i] === START_HOLE && !isScore ? { background: colors.startHole } : null),
              ...(isScore
                ? {
                    height: '3em',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: HAND,
                    fontSize: '1.9em',
                    color: colors.pencil,
                    lineHeight: 1,
                    padding: 0,
                    borderBottom: 0,
                  }
                : null),
            }}
          >
            {v}
          </div>
        ))}
        <div style={{ ...(isScore ? { ...cell, borderRight: 0, borderBottom: 0 } : headCell), borderRight: 0 }}>
          {total}
        </div>
      </>
    );
  };

  return (
    <div
      role="img"
      aria-label="The front nine of Team 7's printed scorecard, started on hole 1, scores pencilled in through hole 5"
      style={{
        background: colors.paper,
        color: colors.paperInk,
        borderRadius: 3,
        boxShadow: '0 1px 1px rgba(0,0,0,.08), 0 12px 32px -8px rgba(16,40,24,.28)',
        padding: isMobile ? '10px 10px 9px' : '14px 14px 12px',
        fontSize: u,
        lineHeight: 1.25,
        transform: isMobile ? 'rotate(-1.4deg)' : 'rotate(-2.2deg)',
        maxWidth: '100%',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.35em', letterSpacing: '0.01em' }}>Lowcountry Charity Classic</div>
          <div style={{ color: colors.gray600 }}>Harbor Course &middot; {SAMPLE_DATE_TEXT}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <b style={{ display: 'block', fontSize: '1.6em', letterSpacing: '0.02em' }}>TEAM 7</b>
          Start: Hole 1A &middot; 8:30 AM shotgun
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', margin: '0 0 8px', color: colors.gray700 }}>
        <span>J. Hendrix</span><span>M. Carroll</span><span>T. Blake</span><span>R. Ruiz</span>
        <span style={{ color: colors.gray500 }}>Gross &mdash; no handicap</span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '6.2em repeat(9, minmax(0, 1fr)) 2.6em',
          border: `1.2px solid ${colors.gray700}`,
        }}
      >
        {gridRow('HOLE', null, HOLES, { total: 'OUT', isHead: true })}
        {gridRow('PAR', null, PARS, { total: 36 })}
        {gridRow('HCP', null, HCPS, { total: '' })}
        {gridRow('TEAM SCORE', 'gross', HOLES.map((h) => PENCILLED[h] || ''), { total: '', isScore: true })}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginTop: 10 }}>
        <div style={{ flex: 1, borderTop: `0.8px solid ${colors.gray700}`, paddingTop: 2, color: colors.gray500, fontSize: '0.9em', marginBottom: '0.4em' }}>
          Attest
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <SampleQr px="5.6em" />
          <div>
            <span style={{ fontFamily: MONO, fontWeight: 500, fontSize: '1.25em', letterSpacing: '0.06em', display: 'block' }}>482 915</span>
            Scan to keep score<br />or {TEAM_CODE_HOST}
            <span style={{ display: 'block', fontWeight: 700, marginTop: 2 }}>
              Live scores: {PUBLIC_BOARD_HOST}/charity-classic
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/** A printed cart sign — one per cart, the two riders and the starting hole. */
const CartSign = ({ isMobile }) => {
  const W = isMobile ? 330 : 440;
  const u = W / 100; // 1 "unit" = 1% of the sign's width

  return (
    <div
      role="img"
      aria-label="A printed cart sign for Team 7, starting on hole 1A"
      style={{
        background: colors.paper,
        color: colors.paperInk,
        width: '100%',
        maxWidth: W,
        aspectRatio: '8.5 / 5.5',
        borderRadius: 3,
        boxShadow: '0 1px 1px rgba(0,0,0,.08), 0 14px 34px -10px rgba(16,40,24,.3)',
        padding: `${5.5 * u}px ${6 * u}px`,
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gridTemplateRows: 'auto 1fr auto',
        gap: `${2 * u}px ${5 * u}px`,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ gridColumn: '1 / -1', fontSize: 4.2 * u, lineHeight: 1.2 }}>
        <b style={{ display: 'block', fontSize: 5 * u }}>Lowcountry Charity Classic</b>
        <span style={{ color: colors.gray600 }}>Harbor Course &middot; {SAMPLE_DATE_TEXT}</span>
      </div>

      <div style={{ alignSelf: 'center' }}>
        <div style={{ fontWeight: 700, fontSize: 13 * u, lineHeight: 0.95, letterSpacing: '0.01em' }}>TEAM 7</div>
        <div style={{ fontWeight: 700, fontSize: 6.4 * u, marginTop: 1.5 * u }}>START &middot; HOLE 1A</div>
        <div style={{ fontSize: 3.8 * u, color: colors.gray700 }}>8:30 AM shotgun</div>
        <div style={{ marginTop: 3 * u, fontSize: 4.9 * u, fontWeight: 600, lineHeight: 1.25 }}>
          J. Hendrix &nbsp;&middot;&nbsp; M. Carroll
          <small style={{ display: 'block', fontWeight: 400, fontSize: 3.3 * u, color: colors.gray600 }}>White tees</small>
        </div>
      </div>

      <div style={{ alignSelf: 'center', textAlign: 'center', fontSize: 2.8 * u, lineHeight: 1.3 }}>
        <div style={{ margin: `0 auto ${1.2 * u}px` }}>
          <SampleQr px={24 * u} />
        </div>
        <span style={{ fontFamily: MONO, fontWeight: 500, fontSize: 4 * u, letterSpacing: '0.05em', display: 'block' }}>482 915</span>
        Scan to keep score<br />or {SAMPLE_SCORING_SHORT}
      </div>

      <div
        style={{
          gridColumn: '1 / -1',
          display: 'flex',
          alignItems: 'center',
          gap: 2 * u,
          fontSize: 3.1 * u,
          borderTop: `0.6px solid ${colors.gray300}`,
          paddingTop: 1.6 * u,
        }}
      >
        <img src="/logo.png" alt="" loading="lazy" width="24" height="24" style={{ width: 5.6 * u, height: 5.6 * u, objectFit: 'contain' }} />
        Live leaderboard: {PUBLIC_BOARD_HOST}/charity-classic
        <span style={{ marginLeft: 'auto', color: colors.gray500 }}>Cart 1 of 2</span>
      </div>
    </div>
  );
};

// The board model the two illustrations render — shaped from the fixture's real
// board rows (the ones /try/leaderboard hands the production TV renderer), so the
// hero phone, the TV panel and the full board can never disagree. `name` is the
// product's team label (surnames A→Z, ' / '); `start` is the product's sub-line
// text ("Start 3"). Team 7 is "yours" everywhere, including on the printed pieces.
const fmtVsPar = (v) => (v == null ? '—' : v === 0 ? 'E' : v > 0 ? `+${v}` : `−${Math.abs(v)}`);

// Same colour rule the live board uses (TeamBoardRows in App.js), so the hero
// shows the product as it actually looks: under par green, level grey, over par
// muted. Don't "improve" this without changing the board too.
const vsParColor = (v) => (v == null ? colors.gray400 : v < 0 ? colors.green800 : v > 0 ? colors.gray400 : colors.gray500);

const SAMPLE_BOARD_MODEL = {
  isNet: false,
  rows: SAMPLE_ROWS.map((r) => ({
    pos: r.position_label || '–',
    name: r.name,
    team: r.team_number,
    start: r.start_text || null,
    v: r.gross_vs_par,
    total: r.gross_total,
    thru: r.has_scores ? r.thru : null,
    mine: r.team_number === SAMPLE_TEAM_NUMBER,
  })),
  eventName: SAMPLE_BOARD.event_name,
  courseName: SAMPLE_BOARD.course_name,
};

// Sub-line under a team's name, the same parts the real renderers assemble:
// team number, then the shotgun start hole. The phone adds Thru because it has
// no Thru column; the TV leaves it out because it does.
const teamSub = (r, withThru) =>
  [r.team != null ? `Team ${r.team}` : null, r.start || null,
   withThru ? (r.thru == null ? 'No scores yet' : `Thru ${r.thru}`) : null]
    .filter(Boolean).join(' · ');

const fmtThru = (thru, holes = 18) => (thru == null ? '–' : thru >= holes ? 'F' : String(thru));

/**
 * The hero phone — the player's view of the board, from a cart.
 * Mirrors TeamBoardRows in App.js: the TEAM LABEL leads (surnames, not "Team 7"),
 * the team number drops to the sub-line, the running total is the big number and
 * to-par is the small coloured one. Under par is GREEN here because that is what
 * the web board renders. The TV board below is RED on purpose; see TvBoard.
 */
const HeroBoard = ({ isMobile, board }) => {
  const { isNet, rows: allRows, eventName, courseName } = board;
  const rows = allRows.slice(0, 5);

  const title = eventName;
  const sub = [courseName, isNet ? 'Net' : 'Captain’s choice'].filter(Boolean).join(' · ');

  const colTemplate = '20px minmax(0, 1fr) 30px 26px';

  return (
    <div
      style={{
        position: 'relative',
        margin: isMobile ? '6px 2% 0 auto' : '6px 4% 0 auto',
        width: isMobile ? 220 : 248,
        background: '#0b0f0c',
        borderRadius: 30,
        padding: 9,
        boxShadow: '0 24px 48px -12px rgba(0,0,0,.45)',
        transform: isMobile ? 'rotate(2deg)' : 'rotate(3deg)',
      }}
    >
      <div style={{ background: '#fff', borderRadius: 22, overflow: 'hidden', color: colors.gray900, fontSize: 11.5 }}>
        <div style={{ background: `linear-gradient(135deg, ${colors.green700}, ${colors.green900})`, color: '#fff', padding: '18px 12px 10px' }}>
          <b style={{ display: 'block', fontSize: 13, lineHeight: 1.2 }}>{title}</b>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10.5, opacity: 0.92, marginTop: 3 }}>
            <i style={{ width: 7, height: 7, borderRadius: '50%', background: colors.amber400, display: 'inline-block', flexShrink: 0 }} />
            Example · {sub}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: colTemplate, alignItems: 'center', padding: '6px 10px', gap: 5, fontSize: 8.5, color: colors.gray500, textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: `1px solid ${colors.gray200}` }}>
          <span>Pos</span><span>Team</span>
          <span style={{ textAlign: 'right' }}>Tot</span>
          <span style={{ textAlign: 'right' }}>Par</span>
        </div>

        <div>
          {rows.map((r, i) => (
            <div
              key={`${r.pos}-${r.name}-${i}`}
              style={{
                display: 'grid',
                gridTemplateColumns: colTemplate,
                alignItems: 'center',
                padding: '6px 10px',
                gap: 5,
                borderBottom: '1px solid #f1f5f2',
                fontVariantNumeric: 'tabular-nums',
                background: r.mine ? colors.green50 : 'transparent',
              }}
            >
              <span style={{ color: colors.gray500, fontWeight: 600 }}>{r.pos}</span>
              <span style={{ minWidth: 0 }}>
                <b style={{ display: 'block', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</b>
                <small style={{ display: 'block', color: colors.gray500, fontSize: 9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {teamSub(r, true)}
                </small>
              </span>
              <span style={{ fontWeight: 700, fontSize: 13, color: colors.gray900, textAlign: 'right' }}>{r.total != null ? r.total : '–'}</span>
              <span style={{ fontWeight: 600, fontSize: 10, color: vsParColor(r.v), textAlign: 'right' }}>{fmtVsPar(r.v)}</span>
            </div>
          ))}
        </div>

        <a
          href={SAMPLE_SCORING_LINK}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('events_try_scoring_click', { location: 'hero_phone' })}
          style={{ display: 'block', fontSize: 10, fontWeight: 700, color: colors.green800, textAlign: 'center', padding: '8px 6px 10px', textDecoration: 'none' }}
        >Example board · try the scoring screen →</a>
      </div>
    </div>
  );
};

// The clubhouse TV view. These are the colours and the column order the real TV
// renderer uses (PublicLeaderboardTv in App.js): Pos · Team · Thru · To par · Total,
// dark field, gold for the leader, and under par in RED inside a pill — broadcast
// convention, and a deliberate difference from the phone board above. Do not
// "correct" it to green; change the TV renderer first if that ever moves.
const TV_BG = 'linear-gradient(180deg, #1f7048 0%, #185c3b 100%)';
const TV_HEADER_BG = '#0d4a2b';
const TV_RED = '#ff6b6b';
const TV_GOLD = '#fcd34d';
const TV_PANEL = 'rgba(255,255,255,0.10)';
const TV_BAND = 'rgba(0,0,0,0.22)';
const TV_LINE = 'rgba(255,255,255,0.20)';
const TV_DIM = 'rgba(255,255,255,0.6)';

const TvBoard = ({ isMobile, board }) => {
  const { isNet, rows: allRows, eventName, courseName } = board;
  const rows = allRows.slice(0, isMobile ? 6 : 10);
  const cols = isMobile
    ? '30px minmax(0, 1fr) 30px 52px 34px'
    : '40px minmax(0, 1fr) 40px 66px 44px';
  const head = { color: TV_DIM, fontSize: isMobile ? 9 : 11, letterSpacing: '0.12em', fontWeight: 700, textTransform: 'uppercase' };

  return (
    <div style={{
      background: '#0b0f0c', borderRadius: isMobile ? 10 : 14, padding: isMobile ? 7 : 11,
      boxShadow: '0 30px 60px -20px rgba(0,0,0,.55)',
    }}>
      <div style={{ background: TV_BG, borderRadius: isMobile ? 6 : 8, overflow: 'hidden' }}>
        <div style={{
          background: TV_HEADER_BG, padding: isMobile ? '12px' : '16px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              color: '#fff', fontWeight: 700, fontSize: isMobile ? 14 : 19, letterSpacing: '0.02em',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{eventName}</div>
            <div style={{ color: TV_DIM, fontSize: isMobile ? 10 : 12, marginTop: 2 }}>
              {[courseName, isNet ? 'NET' : 'GROSS'].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div style={{ color: TV_GOLD, fontWeight: 700, fontSize: isMobile ? 10 : 12, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
            EXAMPLE
          </div>
        </div>

        <div style={{ padding: isMobile ? 8 : 12 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: cols, gap: isMobile ? 6 : 10, alignItems: 'center',
            padding: isMobile ? '6px 10px' : '8px 16px', marginBottom: 8,
            background: TV_BAND, borderRadius: 6,
          }}>
            <span style={head}>Pos</span>
            <span style={head}>Team</span>
            <span style={{ ...head, textAlign: 'center' }}>Thru</span>
            <span style={{ ...head, textAlign: 'right' }}>{isNet ? 'Net' : 'To par'}</span>
            <span style={{ ...head, textAlign: 'right' }}>Total</span>
          </div>

          <div style={{ background: TV_PANEL, borderRadius: 6, overflow: 'hidden' }}>
            {rows.map((r, i) => {
              const leader = r.pos === '1' || r.pos === 'T1';
              return (
                <div key={`${r.pos}-${r.name}-${i}`} style={{
                  display: 'grid', gridTemplateColumns: cols, gap: isMobile ? 6 : 10,
                  padding: isMobile ? '7px 10px' : '9px 16px', alignItems: 'center',
                  fontVariantNumeric: 'tabular-nums',
                  borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${TV_LINE}`,
                }}>
                  <span style={{ color: leader ? TV_GOLD : '#fff', fontWeight: 800, fontSize: isMobile ? 13 : 18 }}>{r.pos}</span>
                  <span style={{ minWidth: 0 }}>
                    <div style={{
                      color: '#fff', fontWeight: 700, fontSize: isMobile ? 12 : 16, letterSpacing: '0.03em',
                      textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{r.name}</div>
                    <div style={{
                      color: TV_DIM, fontSize: isMobile ? 9 : 11, marginTop: 1,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{teamSub(r, false)}</div>
                  </span>
                  <span style={{ textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: isMobile ? 11 : 15 }}>{fmtThru(r.thru)}</span>
                  <span style={{ textAlign: 'right' }}>
                    <span style={{
                      display: 'inline-block', minWidth: isMobile ? 38 : 54, textAlign: 'center',
                      padding: isMobile ? '2px 5px' : '4px 8px', borderRadius: 6,
                      background: r.v != null && r.v < 0 ? 'rgba(255,107,107,0.14)' : 'transparent',
                      color: r.v != null && r.v < 0 ? TV_RED : '#fff',
                      fontWeight: 800, fontSize: isMobile ? 13 : 18, lineHeight: 1.2,
                    }}>{fmtVsPar(r.v)}</span>
                  </span>
                  <span style={{ textAlign: 'right', color: 'rgba(255,255,255,0.95)', fontWeight: 600, fontSize: isMobile ? 12 : 16 }}>
                    {r.total != null ? r.total : '–'}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: TV_DIM,
            fontSize: isMobile ? 9 : 11, padding: isMobile ? '8px 10px 2px' : '12px 16px 2px',
          }}>
            <span style={{ fontFamily: MONO }}>Example board</span>
            <a
              href={SAMPLE_BOARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('events_live_board_click', { location: 'tv_panel' })}
              style={{ color: TV_GOLD, fontWeight: 700, textDecoration: 'none' }}
            >Open the full TV board →</a>
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQ_ITEMS = [
  {
    q: 'Is this a subscription?',
    a: ['Not the Event Pass. You pay once for one event, any date within 6 months of buying it. Finalizing your results uses the pass; the results and the leaderboard then stay viewable for 30 days. Your account stays on the free plan and nothing is deleted, so next year you buy another pass and everything is where you left it.'],
  },
  {
    q: 'Do we have to take registrations through you?',
    a: ['Not today — keep the sign-up form or fundraising site you already use and send us the field. What we will never do is take the money: entry fees go straight to you, however you choose to collect them.'],
  },
  {
    q: 'How do players get in to keep score?',
    a: [`Players already on your list get a personal link in their reminder email, and that works for every format. A scramble has two more doors: every card and cart sign carries that team’s QR code, and failing both, ${TEAM_CODE_HOST} plus the six-digit code printed beside it. No app, no password, no account.`],
  },
  {
    q: 'What formats can we run?',
    a: ['Captain’s choice (scramble), gross or net, with team handicaps on the USGA allowances. Stroke play — gross, net or both — with official placings and ties settled by the USGA scorecard playoff or your committee. Best ball, 2-man or 4-man (best 1, 2 or 3 of 4 on each hole), with a blind draw to fill a short team. Shamble, 2- or 4-person, on the USGA selected-drive allowances. Skins, closest to the pin and Stableford alongside any of them. Tell us what your event runs and we’ll tell you straight whether it fits today.'],
  },
  {
    q: 'What if it rains?',
    a: ['Move the date and the pass moves with it. If you cancel the event before any pairings are saved, we’ll refund the pass.'],
  },
  {
    q: 'What paper do we need?',
    a: ['Cards and signs print two to a sheet on standard perforated golf card stock (scorecards: golfcardstock.com 411-2PP; cart signs: CSW-2PP) or on plain cardstock cut at the same lines. Allow 7–10 business days to order it. Print at 100%.'],
  },
  {
    q: 'Will scores post to GHIN?',
    a: ['Scrambles and other one-score-per-team formats aren’t handicap rounds, so nobody is prompted to post. For individual formats, posting stays each player’s responsibility. We never post on anyone’s behalf.'],
  },
  {
    q: 'Who helps us on the morning of the event?',
    a: ['You get an event-day checklist ahead of time, and we agree before your date how to reach us on the morning. Your pro shop can also be given an event desk — one event’s dashboard, reached with a code, so a pro or a volunteer can print, put the board on the TV, and fix a score without touching anything else.'],
  },
];

const Faq = ({ isMobile }) => (
  <div style={{ borderTop: `1px solid ${colors.gray200}` }}>
    {FAQ_ITEMS.map((item, i) => (
      <details key={item.q} open={i === 0} style={{ borderBottom: `1px solid ${colors.gray200}` }}>
        <summary
          className="tto-ev-summary"
          style={{
            cursor: 'pointer',
            padding: '18px 36px 18px 0',
            fontWeight: 600,
            color: colors.gray900,
            fontSize: isMobile ? 16 : 17,
            position: 'relative',
            listStyle: 'none',
          }}
        >
          {item.q}
        </summary>
        <div style={{ padding: '0 0 18px', display: 'grid', gap: 10, fontSize: 15.5, color: colors.gray600, lineHeight: 1.7, maxWidth: '40em' }}>
          {item.a.map((p) => <p key={p} style={{ margin: 0 }}>{p}</p>)}
        </div>
      </details>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// One click-to-play video card. Until the visitor presses play only the
// thumbnail is on the page; then a youtube-nocookie iframe takes its place.
const VideoCard = ({ video, isMobile }) => {
  const [playing, setPlaying] = useState(false);
  const play = () => {
    trackEvent('events_video_play', { video: video.key, via: 'page' });
    setPlaying(true);
  };
  const frameBox = { position: 'relative', width: '100%', aspectRatio: '16 / 9', background: '#0f2a1c', flexShrink: 0 };
  const fill = { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: 'block' };
  return (
    <article style={{
      background: '#fff', border: `1px solid ${colors.gray200}`, borderRadius: 16, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      {playing ? (
        <div style={frameBox}>
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={fill}
          />
        </div>
      ) : (
        <div style={frameBox}>
        <button
          type="button"
          onClick={play}
          aria-label={`Play video: ${video.title} (${video.duration})`}
          style={{ ...fill, padding: 0, background: 'transparent', cursor: 'pointer' }}
        >
          <img
            src={video.thumb}
            alt=""
            width="1280"
            height="720"
            loading="lazy"
            decoding="async"
            style={{ ...fill, objectFit: 'cover' }}
          />
          <span style={{
            position: 'absolute', right: isMobile ? 10 : 14, bottom: isMobile ? 10 : 14,
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#fff', color: colors.green900, fontFamily: SANS,
            fontSize: isMobile ? 14 : 15, fontWeight: 700,
            padding: isMobile ? '8px 14px 8px 10px' : '9px 16px 9px 12px',
            borderRadius: 999, boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          }}>
            <svg width={isMobile ? 16 : 18} height={isMobile ? 16 : 18} viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5.5v13l10.5-6.5z" fill={colors.green800} />
            </svg>
            Play &middot; {video.duration}
          </span>
        </button>
        </div>
      )}
      <div style={{ padding: isMobile ? '18px 18px 20px' : '22px 24px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.green700 }}>
          {video.kicker}
        </div>
        <h3 style={{ margin: 0, fontSize: isMobile ? 19 : 21, fontWeight: 700, color: colors.gray900 }}>{video.title}</h3>
        <p style={{ margin: 0, fontSize: isMobile ? 15 : 15.5, lineHeight: 1.65, color: colors.gray600 }}>{video.description}</p>
        <a
          href={`https://youtu.be/${video.youtubeId}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('events_video_play', { video: video.key, via: 'youtube' })}
          style={{
            alignSelf: 'flex-start', marginTop: 4, padding: isMobile ? '10px 0' : '4px 0',
            fontSize: 14, fontWeight: 600, color: colors.green800, textDecoration: 'none',
          }}
        >
          Watch on YouTube
        </a>
      </div>
    </article>
  );
};

const EventsPage = () => {
  const navigate = useNavigate();
  const auth = useAuth() || {};
  const signedIn = !!auth.isAuthenticated;
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const sampleBoard = SAMPLE_BOARD_MODEL;

  const [form, setForm] = useState({
    name: '', email: '', eventName: '', eventDate: '', fieldSize: '73–144 players', registration: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Sept 21, 2026 — bot defenses. Neither is visible to a person. `renderedAt`
  // lets the server see the form was actually open for a few seconds; `botField`
  // backs the hidden input below, which only a script fills. The rules live in
  // backend/src/routes/contactRoutes.js — this side only supplies the evidence.
  const [renderedAt] = useState(() => Date.now());
  const [botField, setBotField] = useState('');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Per-route meta + Product/Offer JSON-LD, restored on unmount.
  useEffect(() => {
    applyMeta(EVENTS_META);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'tto-events-jsonld';
    script.text = JSON.stringify(EVENTS_JSONLD);
    document.head.appendChild(script);
    const videoLd = buildVideoJsonLd([PROMO_VIDEO, ...EVENT_VIDEOS].filter((v) => v.youtubeId));
    if (videoLd.length) {
      const vscript = document.createElement('script');
      vscript.type = 'application/ld+json';
      vscript.id = 'tto-events-video-jsonld';
      vscript.text = JSON.stringify(videoLd);
      document.head.appendChild(vscript);
    }
    return () => {
      applyMeta(HOME_META);
      ['tto-events-jsonld', 'tto-events-video-jsonld'].forEach((id) => {
        const existing = document.getElementById(id);
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      });
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Event Pass doors (Sept 30, 2026). The dashboard opens the Event Pass window
  // once when it finds this flag; /dashboard sends a signed-out visitor through
  // /login and back. A stranger with no account goes to /register?event=1, which
  // sets the same flag itself after signup.
  const openPassOnDashboard = () => {
    try { localStorage.setItem('tto_open_event_pass', '1'); } catch (e) { /* storage blocked */ }
    navigate('/dashboard');
  };
  const buyEventPass = (location) => {
    trackEvent('events_buy_click', { location, signed_in: signedIn });
    if (signedIn) openPassOnDashboard();
    else navigate('/events/start');   // Sept 30, 2026: the Event Pass door — account + payment in one screen
  };
  const addEventToGroup = (location) => {
    trackEvent('events_buy_click', { location, signed_in: signedIn });
    openPassOnDashboard();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    trackEvent('events_contact_submit', { field_size: form.fieldSize });

    const message = [
      `Event: ${form.eventName || '(not given)'}`,
      `Date: ${form.eventDate || '(not given)'}`,
      `Expected field: ${form.fieldSize}`,
      '',
      'How golfers sign up today:',
      form.registration || '(not given)',
    ].join('\n');

    try {
      const resp = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: `Event inquiry — ${form.eventName || 'unnamed event'}`,
          message,
          // Structured as well as in the message text, so the server can judge a
          // date like the 1970-05-31 the bot sent without parsing free text.
          eventDate: form.eventDate,
          website: botField,
          formRenderedAt: renderedAt,
        }),
      });
      if (resp.ok) {
        setSent(true);
        setForm({ name: '', email: '', eventName: '', eventDate: '', fieldSize: '73–144 players', registration: '' });
      } else {
        setError('That didn’t send. Please try again, or email support@teetimeorganizer.net.');
      }
    } catch (err) {
      setError('That didn’t send. Please try again, or email support@teetimeorganizer.net.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- shared style fragments -------------------------------------------
  const wrap = { maxWidth: 1120, margin: '0 auto', padding: isMobile ? '0 20px' : '0 32px' };
  const band = { padding: isMobile ? '52px 0' : '72px 0' };
  const bandAlt = { ...band, background: colors.gray50 };

  const eyebrow = {
    display: 'inline-block', background: colors.green100, color: colors.green800,
    fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
    padding: '6px 14px', borderRadius: 20,
  };
  const h2 = {
    fontFamily: SERIF, fontWeight: 400, color: colors.gray900,
    fontSize: isMobile ? 28 : 36, lineHeight: 1.15, letterSpacing: '-0.01em', margin: '12px 0 0',
  };
  const lead = { fontSize: 17, color: colors.gray600, lineHeight: 1.7, margin: '12px 0 0' };

  const btnPrimary = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '13px 20px', fontSize: 15, fontWeight: 600, fontFamily: SANS, borderRadius: 10,
    border: '1px solid transparent', cursor: 'pointer', textDecoration: 'none',
    background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: '#fff',
    boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)', whiteSpace: 'nowrap',
  };
  const btnOutline = {
    ...btnPrimary, background: '#fff', color: colors.green800,
    border: `1px solid ${colors.green700}`, boxShadow: 'none',
  };
  const btnGhost = {
    ...btnPrimary, background: 'transparent', color: colors.green800,
    border: `1px solid ${colors.gray300}`, boxShadow: 'none',
  };

  const card = {
    background: '#fff', border: `1px solid ${colors.gray200}`, borderRadius: 16,
    padding: 26, display: 'grid', gap: 16, alignContent: 'start',
  };
  const inputStyle = {
    width: '100%', padding: '11px 12px', fontSize: 15, color: colors.gray900,
    border: `1px solid ${colors.gray300}`, borderRadius: 10, outline: 'none',
    boxSizing: 'border-box', fontFamily: SANS, background: '#fff',
  };
  const labelStyle = { fontSize: 14, fontWeight: 600, color: colors.gray900, marginBottom: 6, display: 'block' };

  // Each step carries ONE idea in `p` and its supporting detail in `d`. Resist
  // stacking three capabilities into one paragraph — the card stops being
  // scannable and the step names stop meaning anything.
  const steps = [
    {
      n: 1, h: 'Load the field',
      p: 'Enter players yourself, upload a spreadsheet of teams, or share a sign-up link and let them add themselves. Nobody needs an account, and there is nothing to download.',
      d: 'Teams you upload or pair stay together all the way to the printed card.',
    },
    {
      n: 2, h: 'Pair and print',
      p: 'Fit the field to a shotgun you configure, or run it off ordinary tee times. Adjust the pairings, send them to the players, then print a scorecard for every team and a sign for every cart.',
      d: 'Reverse shotgun from hole 1, then 18, 17, 16… with A and B groups when the field needs them.',
    },
    {
      n: 3, h: 'Scan to score',
      p: 'Players score on their own phones \u2014 from the link in their reminder email, or in a scramble from the QR code on the team\u2019s card or the short address beside it.',
      d: 'In a scramble one scorer enters the team\u2019s score; in stroke play and best ball each player keeps his own. No app, no password, and the paper card stays the official record.',
    },
    {
      n: 4, h: 'Watch the board',
      p: `Your event gets its own address on ${PUBLIC_BOARD_HOST}. Players follow it from the course, you put it up on the clubhouse TV, and it moves as the cards come in.`,
      d: 'Results are final the moment the last card is in.',
    },
  ];

  const features = [
    {
      h: 'Scorecards and cart signs, printed',
      p: 'Your event’s name across the top, each team’s starting hole highlighted in yellow, stroke holes marked when handicaps are in play. Cards come out in the order the carts leave.',
      icon: <><path d="M6 9V3h12v6" /><rect x="3" y="9" width="18" height="8" rx="2" /><path d="M6 14h12v7H6z" /></>,
    },
    {
      h: 'A code for every team, no app',
      p: 'In a scramble, players scan their team’s code and score in their phone’s browser. Codes open the morning of the event and switch to the results when it’s over.',
      icon: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><path d="M14 14h3v3h-3zM20 14v.01M14 20h.01M17 20h4v-3" /></>,
    },
    {
      h: 'A leaderboard on every screen',
      p: 'Phones in the carts, the laptop at the scoring table, the big screen in the grill room. Your event keeps its own address, so anyone can follow from anywhere.',
      icon: <><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4M6 13l3-3 3 2 5-5" /></>,
    },
    {
      h: 'Someone else can run the day',
      p: 'Give your pro shop or a volunteer an event code and they get a desk for that one event — print the cards and signs, put the board on the screen, fix a score off a paper card. The rest of your organization stays private.',
      icon: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="12" cy="10" r="2.4" /><path d="M7.5 17.5a4.5 4.5 0 019 0" /></>,
    },
    {
      h: 'The games you’re running',
      p: 'Captain’s choice (scramble), stroke play (gross, net or both), best ball and shamble (2-man, or best 1, 2 or 3 of 4), with skins, closest to the pin and Stableford alongside. Official placings, ties settled by the USGA scorecard playoff or your committee. More formats are on the way — ask us what your event needs.',
      icon: <><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0z" /><path d="M17 5h3v2a3 3 0 01-3 3M7 5H4v2a3 3 0 003 3" /></>,
    },
    {
      h: 'Handicaps handled right',
      p: 'USGA allowances built in: team handicaps for a scramble, with the team’s stroke holes marked on its card, and playing handicaps for stroke play and best ball. Scrambles never prompt anyone to post to GHIN; stroke play and best ball are handicap rounds, and posting stays each player’s call.',
      icon: <><path d="M12 3l8 4v5c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V7z" /><path d="M9 12l2 2 4-4" /></>,
    },
    {
      h: 'Reminders out, tee sheet to the pro shop',
      p: 'Email and text reminders with the forecast, and the tee sheet in the shop’s hands before anyone arrives. The same machinery that runs a weekly group, pointed at your event.',
      icon: <><path d="M4 5h16v14H4z" /><path d="m4 6 8 6 8-6" /></>,
    },
  ];

  return (
    <div style={{ fontFamily: SANS, color: colors.gray600, background: '#fff' }}>
      {/* Styles that inline objects can't express: the FAQ marker, the live dot,
          and the printed-card paper texture. */}
      <style>{`
        .tto-ev-summary::-webkit-details-marker { display: none; }
        .tto-ev-summary::marker { content: ''; }
        .tto-ev-summary::after {
          content: '+'; position: absolute; right: 4px; top: 12px;
          font-size: 24px; font-weight: 400; color: ${colors.green800};
          transition: transform .15s ease;
        }
        details[open] > .tto-ev-summary::after { transform: rotate(45deg); }
        @keyframes tto-ev-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(74,222,128,.7); }
          70%  { box-shadow: 0 0 0 7px rgba(74,222,128,0); }
          100% { box-shadow: 0 0 0 0 rgba(74,222,128,0); }
        }
        .tto-ev-dot { animation: tto-ev-pulse 2s infinite; }
        @media (prefers-reduced-motion: reduce) { .tto-ev-dot { animation: none; } }
        /* The nav is fixed — keep in-page anchors from landing underneath it. */
        #how, #tour, #pricing, #contact { scroll-margin-top: 100px; }
        @media (max-width: 767px) { #how, #tour, #pricing, #contact { scroll-margin-top: 124px; } }
      `}</style>

      {/* ---------------------------------------------------------------- Nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(255, 255, 255, 0.97)', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${colors.gray200}`, padding: isMobile ? '10px 16px' : '12px 32px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SpaLink to="/" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14, textDecoration: 'none' }}>
            <div style={{
              width: isMobile ? 40 : 52, height: isMobile ? 40 : 52, background: '#fff', borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', border: `1px solid ${colors.gray200}`, overflow: 'hidden',
            }}>
              <img src="/logo.png" alt="Tee Time Organizer Logo" width="44" height="44" style={{ width: isMobile ? 32 : 44, height: isMobile ? 32 : 44, objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 800, color: colors.gray900, letterSpacing: '-0.5px' }}>Tee Time Organizer</div>
              {!isMobile && <div style={{ fontSize: 12, color: colors.gray500, fontWeight: 500 }}>Tournaments &amp; Outings</div>}
            </div>
          </SpaLink>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!isMobile && (
              <>
                <ProductsMenu isMobile={false} location="events_header" current="events" />
                <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }} style={{ padding: '10px 18px', fontSize: 14, fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: 8, textDecoration: 'none' }}>Pricing</a>
              </>
            )}
            <button
              onClick={() => { trackEvent('events_cta_click', { location: 'header' }); scrollTo('contact'); }}
              style={{
                padding: isMobile ? '8px 12px' : '12px 24px', fontSize: isMobile ? 12 : 14, fontWeight: 600,
                borderRadius: 10, border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`, color: '#78350f',
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
              }}
            >{isMobile ? 'Contact' : 'Talk to us'}</button>
            <button
              onClick={() => navigate('/login')}
              style={{
                padding: isMobile ? '8px 12px' : '12px 24px', fontSize: isMobile ? 12 : 14, fontWeight: 600,
                borderRadius: 10, border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: '#fff',
                boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)',
              }}
            >{isMobile ? 'Login' : 'Organizer Login'}</button>
          </div>
        </div>
        {/* Phone link row (Sept 25, 2026): the desktop header's two links, always
            visible under the logo row, so a phone visitor can get back to groups. */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, marginTop: 6 }}>
            <ProductsMenu isMobile location="events_header_mobile" current="events" />
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }} style={{ padding: '10px 10px', fontSize: 14, fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: 8, textDecoration: 'none', whiteSpace: 'nowrap' }}>Pricing</a>
          </div>
        )}
      </nav>

      {/* -------------------------------------------------------------- Hero */}
      <section style={{ padding: isMobile ? '136px 0 44px' : '118px 0 72px', overflow: 'hidden' }}>
        <div style={{
          ...wrap,
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.08fr)',
          gap: 48, alignItems: 'center',
        }}>
          <div style={{ display: 'grid', gap: 20, justifyItems: 'start' }}>
            <span style={eyebrow}>Tournaments &middot; outings &middot; member-guests</span>
            <h1 style={{
              fontFamily: SERIF, fontWeight: 400, color: colors.gray900,
              fontSize: isMobile ? 40 : 60, lineHeight: 1.02, letterSpacing: '-0.01em', margin: 0,
            }}>
              Run your tournament&rsquo;s <span style={{ color: colors.green800 }}>day.</span>
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.7, maxWidth: '34em', margin: 0 }}>
              Pairings and shotgun starts in minutes. Scorecards and cart signs printed and ready for the carts.
              Teams scan the code on their card to keep score&nbsp;&mdash; no app, no login&nbsp;&mdash; and a live
              leaderboard runs on every phone and the clubhouse TV.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button
                onClick={() => { trackEvent('events_cta_click', { location: 'hero' }); scrollTo('pricing'); }}
                style={{ ...btnPrimary, border: 'none' }}
              >Get an Event Pass</button>
              <a
                href={SAMPLE_BOARD_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('events_live_board_click', { location: 'hero' })}
                style={btnGhost}
              >See the leaderboard</a>
              <a
                href={SAMPLE_SCORING_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('events_try_scoring_click', { location: 'hero' })}
                style={btnGhost}
              >Try the scoring screen</a>
              {PROMO_VIDEO.youtubeId && (
                <button
                  onClick={() => { trackEvent('events_cta_click', { location: 'hero_watch' }); scrollTo('tour'); }}
                  style={btnGhost}
                >&#9654;&nbsp; Watch the video ({PROMO_VIDEO.duration})</button>
              )}
            </div>
            <p style={{ fontSize: 14, color: colors.gray500, margin: 0 }}>
              <strong style={{ color: colors.gray900 }}>Keeps the registration you already use.</strong>{' '}
              We handle the golf, not the entry fees.
            </p>
          </div>

          <div style={{ position: 'relative', marginTop: isMobile ? 40 : 0 }}>
            <PrintedScorecard isMobile={isMobile} />
            <HeroBoard isMobile={isMobile} board={sampleBoard} />
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- Promo video (1:15) */}
      {PROMO_VIDEO.youtubeId && (
        <section id="tour" style={{ padding: isMobile ? '8px 0 52px' : '0 0 72px' }}>
          <div style={wrap}>
            <div style={{ maxWidth: 880, margin: '0 auto' }}>
              <div style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 28 }}>
                <span style={eyebrow}>See it in action</span>
                <h2 style={h2}>Tournament day in 75 seconds</h2>
              </div>
              <VideoCard video={PROMO_VIDEO} isMobile={isMobile} />
            </div>
          </div>
        </section>
      )}

      {/* ------------------------------------------------- How the day works */}
      <section id="how" style={bandAlt}>
        <div style={wrap}>
          <div style={{ maxWidth: 640, marginBottom: 40 }}>
            <span style={eyebrow}>How the day works</span>
            <h2 style={h2}>From a list of foursomes to a final leaderboard</h2>
            <p style={lead}>The same four steps whether it&rsquo;s 36 players on a Saturday or 144 on a shotgun.</p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(4, minmax(0, 1fr))',
            gap: 20,
          }}>
            {steps.map((s) => (
              <div key={s.n} style={{ ...card, padding: 22, gap: 10 }}>
                <span style={{
                  width: 34, height: 34, borderRadius: '50%', background: colors.green800, color: '#fff',
                  display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 15,
                }}>{s.n}</span>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.gray900, margin: 0 }}>{s.h}</h3>
                <p style={{ fontSize: 15, margin: 0, lineHeight: 1.65 }}>{s.p}</p>
                <p style={{ fontSize: 13, color: colors.gray500, borderTop: `1px solid ${colors.gray200}`, paddingTop: 10, margin: 0 }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- Clubhouse screen */}
      <section style={{ ...band, background: colors.gray900 }}>
        <div style={{
          ...wrap,
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: 'minmax(0, .85fr) minmax(0, 1.15fr)',
          gap: 48, alignItems: 'center',
        }}>
          <div style={{ marginBottom: isMobile ? 32 : 0 }}>
            <span style={{ ...eyebrow, background: 'rgba(255,255,255,0.12)', color: '#f0fdf4' }}>On the clubhouse screen</span>
            <h2 style={{ ...h2, color: '#fff', fontSize: isMobile ? 28 : 38, marginTop: 16 }}>
              The board everyone gathers around
            </h2>
            <p style={{ fontSize: 17, lineHeight: 1.7, color: colors.gray300, marginTop: 16, maxWidth: '32em' }}>
              The same scores your golfers are entering out on the course, up on the television in the grill room
              &mdash; dark field, big type, red under par, the way tournament golf looks on a Sunday afternoon. It
              pages through the whole field so every team comes round.
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: colors.gray400, marginTop: 12, maxWidth: '32em' }}>
              It runs in a browser, so anything that opens a web page can show it. Nobody installs anything and
              nobody has to sit and drive it &mdash; put the address in, go full screen, and leave it alone.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 24 }}>
              <a
                href={SAMPLE_BOARD_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('events_live_board_click', { location: 'tv_section' })}
                style={{ ...btnPrimary, border: 'none' }}
              >See it on the clubhouse TV</a>
            </div>
          </div>

          <TvBoard isMobile={isMobile} board={sampleBoard} />
        </div>
      </section>

      {/* ------------------------------------------------------ On the day */}
      <section style={band}>
        <div style={{
          ...wrap,
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: 'minmax(0, .9fr) minmax(0, 1.1fr)',
          gap: 56, alignItems: 'start',
        }}>
          <div style={{
            display: 'grid', gap: 14, justifyItems: 'center', marginBottom: isMobile ? 40 : 0,
            position: isMobile ? 'static' : 'sticky', top: 100,
          }}>
            <CartSign isMobile={isMobile} />
            <p style={{ fontSize: 13, color: colors.gray500, textAlign: 'center', maxWidth: 440, margin: 0, lineHeight: 1.6 }}>
              The actual cart sign, one per cart. Scorecards and signs print two to a sheet on standard perforated
              golf card stock, or on plain cardstock cut at the same lines.
            </p>
          </div>

          <div>
            <div style={{ maxWidth: 640, marginBottom: 32 }}>
              <span style={eyebrow}>What&rsquo;s on the day</span>
              <h2 style={h2}>Everything the scoring table used to do by hand</h2>
            </div>
            <div>
              {features.map((f, i) => (
                <div
                  key={f.h}
                  style={{
                    display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', gap: 16,
                    padding: i === 0 ? '0 0 22px' : '22px 0',
                    borderBottom: i === features.length - 1 ? 'none' : `1px solid ${colors.gray200}`,
                  }}
                >
                  <span style={{
                    width: 44, height: 44, borderRadius: 12, background: colors.green100, color: colors.green800,
                    display: 'grid', placeItems: 'center',
                  }}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      {f.icon}
                    </svg>
                  </span>
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, color: colors.gray900, margin: '0 0 4px' }}>{f.h}</h3>
                    <p style={{ fontSize: 15, margin: 0, lineHeight: 1.65 }}>{f.p}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------------------------------- See it in action */}
      {EVENT_VIDEOS.some((v) => v.youtubeId) && (
        <section id="watch" style={bandAlt}>
          <div style={wrap}>
            <div style={{ maxWidth: 720, marginBottom: isMobile ? 28 : 40 }}>
              <span style={eyebrow}>See it in action</span>
              <h2 style={h2}>Two short videos: setting it up, and running the day</h2>
              <p style={{ ...lead, fontSize: isMobile ? 16 : 17 }}>
                A few minutes each, following a 48-player, two-man scramble with a shotgun start.
              </p>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
              gap: isMobile ? 28 : 32,
            }}>
              {EVENT_VIDEOS.filter((v) => v.youtubeId).map((v) => (
                <VideoCard key={v.key} video={v} isMobile={isMobile} />
              ))}
            </div>
            <p style={{ margin: isMobile ? '28px 0 0' : '36px 0 0', fontSize: 15.5, lineHeight: 1.6, color: colors.gray600 }}>
              Curious what your players see on their phones?{' '}
              <a
                href={PLAYER_TOUR_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('events_video_play', { video: 'player_tour', via: 'youtube' })}
                style={{ fontWeight: 600, color: colors.green800, textDecoration: 'none' }}
              >
                Take the 4-minute players&rsquo; tour
              </a>
            </p>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------------ Money */}
      <section style={{ ...band, background: colors.green900, color: '#bbf7d0' }}>
        <div style={{
          ...wrap,
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 56, alignItems: 'center',
        }}>
          <div style={{ marginBottom: isMobile ? 36 : 0 }}>
            <span style={{ ...eyebrow, background: 'rgba(255,255,255,0.12)', color: '#f0fdf4' }}>We never touch your money</span>
            <h2 style={{ ...h2, color: '#f0fdf4', fontSize: isMobile ? 30 : 40, lineHeight: 1.1, marginTop: 16 }}>
              Keep every dollar your golfers pay you.
            </h2>
            <p style={{ fontSize: 17, lineHeight: 1.7, maxWidth: '32em', marginTop: 16 }}>
              Take entries however you do today: a church sign-up form, the club&rsquo;s spreadsheet, a fundraising site.
              Tee Time Organizer starts where the money stops.
            </p>
            <p style={{ fontSize: 17, lineHeight: 1.7, maxWidth: '32em', marginTop: 12 }}>
              &ldquo;Free&rdquo; tournament platforms usually get paid out of your entry fees, typically 3&ndash;5% of what
              golfers pay. We charge one flat price for the day instead.
            </p>
          </div>

          <div style={{
            background: 'rgba(0,0,0,.18)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 16,
            padding: 24, fontVariantNumeric: 'tabular-nums',
          }}>
            {[
              { label: '144 golfers × $150 entry', value: '$21,600', color: '#f0fdf4' },
              { label: 'Kept by a 4% registration platform fee', value: '≈ $864', color: '#fca5a5' },
              { label: 'An Event Pass for the same field', value: '$185', color: '#86efac', last: true },
            ].map((r) => (
              <div key={r.label} style={{
                display: 'flex', justifyContent: 'space-between', gap: 16, padding: '10px 0',
                borderBottom: r.last ? 'none' : '1px solid rgba(255,255,255,.12)', alignItems: 'baseline',
              }}>
                <span style={{ fontSize: 15 }}>{r.label}</span>
                <b style={{ color: r.color, fontSize: 22, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.value}</b>
              </div>
            ))}
            <p style={{ fontSize: 12, opacity: 0.8, marginTop: 10 }}>
              Illustration. Platform fees vary; many pass them on to golfers at checkout.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- Pricing */}
      <section id="pricing" style={bandAlt}>
        <div style={wrap}>
          <div style={{ maxWidth: 640, marginBottom: 40 }}>
            <span style={eyebrow}>Pricing</span>
            <h2 style={h2}>Same event day, three ways to buy it</h2>
            <p style={lead}>Choose the one that sounds like your situation.</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(3, minmax(0, 1fr))',
            gap: 20, alignItems: 'start', maxWidth: isMobile ? 560 : 'none',
          }}>
            {/* Card 1 — standalone Event Pass */}
            <article style={{
              ...card,
              border: `2px solid ${colors.green700}`,
              boxShadow: '0 18px 40px -24px rgba(21,128,61,.45)',
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.gray500 }}>
                  A single tournament or outing
                </div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: colors.green800, margin: '4px 0 0' }}>Event Pass</h3>
              </div>

              <div style={{ background: colors.green100, color: colors.green800, borderRadius: 12, padding: '12px 14px', display: 'grid', gap: 2 }}>
                <b style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 28, lineHeight: 1.1 }}>One event</b>
                <span style={{ fontSize: 14, lineHeight: 1.4 }}>Everything for one tournament day, bought when you need it. No subscription.</span>
              </div>

              <div style={{ display: 'grid', gap: 8 }}>
                {[
                  { amount: '135', note: 'up to 72 players' },
                  { amount: '185', note: 'up to 250 players', last: true },
                ].map((p) => (
                  <div key={p.amount} style={{
                    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
                    borderBottom: p.last ? 'none' : `1px dashed ${colors.gray200}`, paddingBottom: p.last ? 0 : 8,
                  }}>
                    <b style={{ fontSize: 34, fontWeight: 700, color: colors.gray900, letterSpacing: '-0.02em' }}>
                      <sup style={{ fontSize: '0.5em', verticalAlign: '0.7em', marginRight: 1 }}>$</sup>{p.amount}
                    </b>
                    <span style={{ fontSize: 14, color: colors.gray600, textAlign: 'right' }}>{p.note}</span>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: 15, margin: 0, lineHeight: 1.65 }}>
                One payment for one event, good for any date within 6 months of purchase.
                If it rains out, the pass moves to your new date.
              </p>

              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 7, fontSize: 14.5 }}>
                {['Field upload, pairings and shotgun',
                  'Printed scorecards and cart signs',
                  'Team codes and the live leaderboard',
                  'Reminders by email and text',
                  'Results and the leaderboard viewable for 30 days'].map((li) => (
                  <li key={li} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 6 }}>
                    <span style={{ color: colors.green800, fontWeight: 700 }}>&#10003;</span>{li}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => buyEventPass('pricing_pass')}
                style={{ ...btnPrimary, border: 'none', width: '100%' }}
              >Buy an Event Pass</button>

              <p style={{ fontSize: 13, color: colors.gray500, margin: 0, lineHeight: 1.6 }}>
                {signedIn
                  ? <>You&rsquo;re signed in, so the pass goes on your group&rsquo;s account.</>
                  : <>Pick your field size, name your event and pay by card. We&rsquo;ll walk you through the setup from there.{' '}
                      Already have an account?{' '}
                      <a href="/login" onClick={(e) => { e.preventDefault(); addEventToGroup('pricing_pass_login'); }} style={{ color: colors.green800 }}>Log in to buy</a>.</>}
                {' '}Questions first?{' '}
                <a href="#contact" onClick={(e) => { e.preventDefault(); trackEvent('events_cta_click', { location: 'pricing_pass' }); scrollTo('contact'); }} style={{ color: colors.green800 }}>Talk to us</a>.
              </p>
            </article>

            {/* Card 2 — existing subscriber */}
            <article style={card}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.gray500 }}>
                  A group that plays all year
                </div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: colors.green800, margin: '4px 0 0' }}>Already use Tee Time Organizer?</h3>
              </div>

              <div style={{ background: colors.green100, color: colors.green800, borderRadius: 12, padding: '12px 14px', display: 'grid', gap: 2 }}>
                <b style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 28, lineHeight: 1.1 }}>Per event</b>
                <span style={{ fontSize: 14, lineHeight: 1.4 }}>Add a tournament to the group you already run.</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                <b style={{ fontSize: 34, fontWeight: 700, color: colors.gray900, letterSpacing: '-0.02em' }}>
                  <sup style={{ fontSize: '0.5em', verticalAlign: '0.7em', marginRight: 1 }}>$</sup>85
                </b>
                <span style={{ fontSize: 14, color: colors.gray600, textAlign: 'right' }}>per event, on Professional or above</span>
              </div>

              <p style={{ fontSize: 15, margin: 0, lineHeight: 1.65 }}>
                Run your member-guest or year-end scramble in the group you already manage. Same pass, your players already loaded.
              </p>

              <div style={{ background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: 10, padding: '10px 12px', fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span>League, billed annually</span>
                  <b style={{ color: colors.gray900, textAlign: 'right' }}>1 event a year included</b>
                </div>
              </div>

              <button
                onClick={() => addEventToGroup('pricing_subscriber')}
                style={{ ...btnOutline, width: '100%' }}
              >Add an event to your group</button>

              <p style={{ fontSize: 13, color: colors.gray500, margin: 0, lineHeight: 1.6 }}>
                {signedIn ? 'Opens the Event Pass in your dashboard.' : 'Log in and the Event Pass opens in your dashboard.'}{' '}
                On Free, Starter or Plus? You can still add an event at the $135 / $185 Event Pass price.{' '}
                <SpaLink to="/groups#pricing" style={{ color: colors.green800, textDecoration: 'underline' }}>See golf group plans</SpaLink>
              </p>
            </article>

            {/* Card 3 — Club & Association */}
            <article style={card}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.gray500 }}>
                  Golf clubs and their associations
                </div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: colors.green800, margin: '4px 0 0' }}>Club &amp; Association</h3>
              </div>

              <div style={{ background: colors.green100, color: colors.green800, borderRadius: 12, padding: '12px 14px', display: 'grid', gap: 2 }}>
                <b style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 28, lineHeight: 1.1 }}>Unlimited events</b>
                <span style={{ fontSize: 14, lineHeight: 1.4 }}>Every tournament, member-guest and championship your club runs, for one price.</span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} aria-label="Built for">
                {['Golf clubs', 'MGA', 'LGA', 'Couples', 'Seniors'].map((c) => (
                  <span key={c} style={{
                    fontSize: 13, fontWeight: 600, color: colors.gray900, background: '#fff',
                    border: `1px solid ${colors.gray300}`, borderRadius: 20, padding: '4px 11px',
                  }}>{c}</span>
                ))}
              </div>

              <p style={{ fontSize: 15, margin: 0, lineHeight: 1.65 }}>
                Priced by conversation, because no two clubs run the same calendar. Tell us what your season looks like.
              </p>

              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 7, fontSize: 14.5 }}>
                {['MGA, LGA and seniors under one club account, with couples events drawn from both',
                  'Each member counted once, however many groups they’re in',
                  '9- and 18-hole divisions on the same day',
                  'A season points race, if you run one'].map((li) => (
                  <li key={li} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 6 }}>
                    <span style={{ color: colors.green800, fontWeight: 700 }}>&#10003;</span>{li}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => { trackEvent('events_cta_click', { location: 'pricing_club' }); scrollTo('contact'); }}
                style={{ ...btnOutline, width: '100%' }}
              >Talk to us about your club</button>
            </article>
          </div>

          <p style={{ marginTop: 24, fontSize: 14, color: colors.gray500 }}>
            Fields over 250 players run on the Club &amp; Association plan.
          </p>
        </div>
      </section>

      {/* -------------------------------------------------------------- FAQ */}
      <section style={band}>
        <div style={{
          ...wrap,
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: 'minmax(0, .8fr) minmax(0, 1.2fr)',
          gap: 56, alignItems: 'start',
        }}>
          <div style={{ maxWidth: 640, marginBottom: isMobile ? 32 : 0 }}>
            <span style={eyebrow}>Questions</span>
            <h2 style={h2}>What organizers ask first</h2>
            <p style={lead}>
              Still wondering about something?{' '}
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('contact'); }} style={{ color: colors.green800 }}>Ask us directly</a>.
              {' '}A person answers.
            </p>
          </div>
          <Faq isMobile={isMobile} />
        </div>
      </section>

      {/* ---------------------------------------------------------- Contact */}
      <section id="contact" style={bandAlt}>
        <div style={{
          ...wrap,
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: 'minmax(0, .9fr) minmax(0, 1.1fr)',
          gap: 56, alignItems: 'start',
        }}>
          <div style={{ marginBottom: isMobile ? 32 : 0 }}>
            <h2 style={{ ...h2, fontSize: isMobile ? 30 : 40, lineHeight: 1.1, marginTop: 0 }}>Tell us about your event</h2>
            <p style={lead}>
              We&rsquo;ll set it up with you, walk you through the day, and make sure your cards are printed before the carts roll.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            style={{
              background: '#fff', border: `1px solid ${colors.gray200}`, borderRadius: 16,
              padding: isMobile ? 20 : 26,
              display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: 16,
            }}
          >
            {/* Bot trap. Off-screen rather than display:none (some scripts skip
                hidden inputs), out of the tab order, and hidden from screen
                readers — so no keyboard or assistive-tech user can reach it.
                Named "website" because that is what a form-filling script looks
                for. Anything here came from a machine. */}
            <input
              type="text"
              name="website"
              value={botField}
              onChange={(e) => setBotField(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />
            {sent ? (
              <div style={{ gridColumn: '1 / -1', background: colors.green50, color: colors.green800, padding: 16, borderRadius: 10, fontSize: 15 }}>
                <b style={{ display: 'block', marginBottom: 4 }}>Thanks &mdash; that&rsquo;s with us.</b>
                We&rsquo;ll be in touch about your event shortly.
              </div>
            ) : (
              <>
                {error && (
                  <div style={{ gridColumn: '1 / -1', background: '#fef2f2', color: '#dc2626', padding: 12, borderRadius: 8, fontSize: 14 }}>{error}</div>
                )}

                <div>
                  <label htmlFor="ev-name" style={labelStyle}>Your name</label>
                  <input id="ev-name" type="text" required autoComplete="name" style={inputStyle}
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="ev-email" style={labelStyle}>Email</label>
                  <input id="ev-email" type="email" required autoComplete="email" style={inputStyle}
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="ev-event" style={labelStyle}>Event name</label>
                  <input id="ev-event" type="text" required style={inputStyle}
                    value={form.eventName} onChange={(e) => setForm({ ...form, eventName: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="ev-date" style={labelStyle}>Event date</label>
                  <input id="ev-date" type="date" style={inputStyle}
                    value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="ev-size" style={labelStyle}>Expected field</label>
                  <select id="ev-size" style={inputStyle}
                    value={form.fieldSize} onChange={(e) => setForm({ ...form, fieldSize: e.target.value })}>
                    <option>Up to 72 players</option>
                    <option>73&ndash;144 players</option>
                    <option>145&ndash;250 players</option>
                    <option>More than 250</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="ev-reg" style={labelStyle}>How do golfers sign up today?</label>
                  <textarea id="ev-reg" style={{ ...inputStyle, minHeight: 84, resize: 'vertical' }}
                    value={form.registration} onChange={(e) => setForm({ ...form, registration: e.target.value })} />
                </div>

                <button type="submit" disabled={submitting} style={{ ...btnPrimary, border: 'none', gridColumn: '1 / -1', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Sending…' : 'Send'}
                </button>
              </>
            )}
          </form>
        </div>
      </section>

      {/* ----------------------------------------------------------- Footer */}
      <footer style={{ padding: isMobile ? '36px 20px 24px' : '52px 32px 28px', background: colors.gray900 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr',
            gap: isMobile ? 28 : 40,
            marginBottom: 32, paddingBottom: 28, borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, background: '#fff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <img src="/logo.png" alt="Tee Time Organizer Logo" loading="lazy" width="32" height="32" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>Tee Time Organizer</div>
              </div>
              <p style={{ fontSize: 13, color: colors.gray400, lineHeight: 1.7, margin: 0, maxWidth: 380 }}>
                Golf tournament and outing software for the day itself: shotgun pairings, printed scorecards and cart
                signs, scan-to-score team codes, and a live leaderboard for phones and the clubhouse TV.
              </p>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 14 }}>Events</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: 10 }}>
                  <a href="#how" onClick={(e) => { e.preventDefault(); scrollTo('how'); }} style={{ color: colors.gray300, fontSize: 14, cursor: 'pointer', textDecoration: 'none' }}>How the day works</a>
                </li>
                <li style={{ marginBottom: 10 }}>
                  <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo('pricing'); }} style={{ color: colors.gray300, fontSize: 14, cursor: 'pointer', textDecoration: 'none' }}>Pricing</a>
                </li>
                <li style={{ marginBottom: 10 }}>
                  <a href={SAMPLE_BOARD_URL} target="_blank" rel="noopener noreferrer" style={{ color: colors.gray300, fontSize: 14, textDecoration: 'none' }}>Example leaderboard &#8599;</a>
                </li>
                <li style={{ marginBottom: 10 }}>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollTo('contact'); }} style={{ color: colors.gray300, fontSize: 14, cursor: 'pointer', textDecoration: 'none' }}>Talk to us</a>
                </li>
              </ul>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 14 }}>Tee Time Organizer</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: 10 }}>
                  <SpaLink to="/groups" style={{ color: colors.gray300, fontSize: 14, cursor: 'pointer', textDecoration: 'none' }}>Weekly Golf Groups</SpaLink>
                </li>
                <li style={{ marginBottom: 10 }}>
                  <SpaLink to="/guides" style={{ color: colors.gray300, fontSize: 14, cursor: 'pointer', textDecoration: 'none' }}>Guides</SpaLink>
                </li>
                <li style={{ marginBottom: 10 }}>
                  <SpaLink to="/login" style={{ color: colors.gray300, fontSize: 14, cursor: 'pointer', textDecoration: 'none' }}>Organizer Login</SpaLink>
                </li>
                <li style={{ marginBottom: 10 }}>
                  <SpaLink to="/privacy" style={{ color: colors.gray300, fontSize: 14, cursor: 'pointer', textDecoration: 'none' }}>Privacy Policy</SpaLink>
                </li>
              </ul>
            </div>
          </div>

          <div style={{
            display: 'flex', flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 8 : 0,
          }}>
            <div style={{ fontSize: 12, color: colors.gray400 }}>&copy; 2025&ndash;2026 Tee Time Organizer. All rights reserved.</div>
            <div style={{ fontSize: 12, color: colors.gray500 }}>Made for golf groups, leagues, and clubs.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EventsPage;
